import { Button, ErrorAlert, cx } from "@unizpay/ui";
import { type ReactNode, useEffect, useId, useRef } from "react";

/**
 * Modal built on the native <dialog>: the browser traps focus, closes on Escape and
 * returns focus to the opener. `onClose` reports a close the parent did not ask for (Escape);
 * closing because `open` became false does not call it again.
 */
export function Dialog({
  open,
  onClose,
  title,
  children,
  footer,
  className,
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  className?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onClose={() => open && onClose()}
      className={cx(
        "m-auto w-[calc(100%-2rem)] max-w-md rounded-lg border border-line bg-surface p-0 text-fg shadow-xl",
        className,
      )}
    >
      {open && (
        <>
          <div className="px-5 pt-5 pb-4">
            <h2 id={titleId} className="text-base font-semibold">
              {title}
            </h2>
            <div className="mt-2 text-[0.8125rem] text-fg-muted">{children}</div>
          </div>
          {footer && (
            <div className="flex flex-wrap justify-end gap-2 border-t border-line bg-subtle/60 px-5 py-3">
              {footer}
            </div>
          )}
        </>
      )}
    </dialog>
  );
}

/** Asks before a destructive or irreversible action. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  danger = false,
  loading = false,
  error,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: ReactNode;
  children: ReactNode;
  confirmLabel: string;
  danger?: boolean;
  loading?: boolean;
  error?: unknown;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Dialog
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div>{children}</div>
        <ErrorAlert error={error} />
      </div>
    </Dialog>
  );
}
